/**
 * 화이트보드 UI 서브 컴포넌트
 * v1 원본: utils/whiteboard/WhiteboardUIComponents.js (600줄)
 * v2 변환: TypeScript 함수형 컴포넌트
 *
 * - PageInfo: 문서공유 상단 헤더 (페이지 이동, 종료)
 * - DrawNavigation: 왼쪽 드로잉 도구 사이드바
 * - PageNavigation: 하단 페이지 썸네일 목록
 * - SketchModeTitle: 스케치 모드 상단 설정 패널
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAppSelector } from '@/store/hooks';
import { BG_COLORS } from '@/utils/whiteboard/constants';

// ─── PageInfo (문서공유 상단 헤더) ─────────────────────────────────────────

interface PageInfoProps {
  currentPage: number;  // 1-indexed (display용)
  totalPages: number;
  pdfFileName: string;
  isOwner: boolean;
  onClickPrev: () => void;
  onClickNext: () => void;
  onClickClose: () => void;
  onMounted?: () => void;
}

export const PageInfo: React.FC<PageInfoProps> = ({
  currentPage,
  totalPages,
  pdfFileName,
  isOwner,
  onClickPrev,
  onClickNext,
  onClickClose,
  onMounted,
}) => {
  useEffect(() => {
    if (onMounted) {
      const timer = setTimeout(() => onMounted(), 100);
      return () => clearTimeout(timer);
    }
  }, [onMounted]);

  const onClickFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      (document.getElementById('fileViewer') ?? document.documentElement).requestFullscreen?.();
    }
  };

  return (
    <div className="viewer_head clearfix" style={{ position: 'relative', zIndex: 600 }}>
      <div className="title_wrap">
        <h2 className="title_filename ellipsis">{pdfFileName}</h2>
      </div>
      <div className="pageinfo_wrap">
        <button type="button" onClick={onClickPrev} className="btn btn_fileprev" disabled={currentPage <= 1}>
          <span className="blind">문서 이전</span>
          <span className="sp_rtc" style={{ verticalAlign: 'middle' }} />
        </button>
        <span className="page_count"><strong>{currentPage}</strong>/{totalPages}</span>
        <button type="button" onClick={onClickNext} className="btn btn_filenext" disabled={currentPage >= totalPages}>
          <span className="blind">문서 다음</span>
          <span className="sp_rtc" style={{ verticalAlign: 'middle' }} />
        </button>
      </div>
      <div className="btngroup_wrap">
        <button type="button" className="LUX_basic_btn Default basic btn_filefull" title="전체화면 보기" onClick={onClickFullscreen}>
          <span className="sp_rtc">전체화면 보기</span>
        </button>
        {isOwner && (
          <button type="button" className="LUX_basic_btn Default basic btn_fileclose" title="문서공유 종료" onClick={onClickClose}>
            <span className="sp_rtc">문서공유 종료</span>
          </button>
        )}
      </div>
    </div>
  );
};

// ─── DrawNavigation (왼쪽 드로잉 도구) ─────────────────────────────────────

interface DrawNavigationProps {
  currentTool: string;
  onClickTools: (type: string) => void;
  isOpenDiagramPopover: boolean;
  setIsOpenDiagramPopover: (v: boolean) => void;
  excalidrawApi?: React.RefObject<{ setActiveTool: (tool: { type: string }) => void } | null>;
  onClickUndo: () => void;
  onClickRedo: () => void;
}

interface ToolButtonProps {
  toolType: string;
  isSelected: boolean;
  onClick: (type: string) => void;
  title: string;
  className: string;
  children?: React.ReactNode;
}

const ToolButton: React.FC<ToolButtonProps> = ({ toolType, isSelected, onClick, title, className, children }) => (
  <button
    type="button"
    className={`viewBtnItem ${className} ${isSelected ? 'selected' : ''}`}
    onClick={() => onClick(toolType)}
    title={title}
  >
    <span className="blind">{children || title}</span>
  </button>
);

interface FigureButtonProps {
  figureType: string;
  isSelected: boolean;
  onClick: (type: string) => void;
  title: string;
  className: string;
}

const FigureButton: React.FC<FigureButtonProps> = ({ figureType, isSelected, onClick, title, className }) => (
  <button
    type="button"
    className={`viewBtnItem ${className} ${isSelected ? 'selected' : ''}`}
    onClick={() => onClick(figureType)}
    title={title}
  >
    <span className="blind">{title}</span>
  </button>
);

export const DrawNavigation: React.FC<DrawNavigationProps> = ({
  currentTool,
  onClickTools,
  isOpenDiagramPopover,
  setIsOpenDiagramPopover,
  excalidrawApi,
  onClickUndo,
  onClickRedo,
}) => (
  <div className="schechMode__sideBox" style={{zIndex:11}}>
    <div className="schechMode__viewBtnItem">
      <ToolButton
        toolType="selection"
        isSelected={currentTool === 'selection'}
        onClick={onClickTools}
        className="viewBtnItem--point"
        title="선택"
      />

      <span className="bar" />

      <div className="viewBtnItem__wrap">
        <ToolButton
          toolType="freedraw"
          isSelected={currentTool === 'freedraw'}
          onClick={onClickTools}
          className="viewBtnItem--pen"
          title="펜"
        />
      </div>
      <div className="viewBtnItem__wrap">
        <ToolButton
          toolType="eraser"
          isSelected={currentTool === 'eraser'}
          onClick={onClickTools}
          className="viewBtnItem--eraser"
          title="지우개"
        />
      </div>
      <div className="viewBtnItem__wrap">
        <ToolButton
          toolType="figure"
          isSelected={isOpenDiagramPopover || ['rectangle', 'ellipse', 'diamond'].includes(currentTool)}
          onClick={() => {
            // 펜 패널 숨김 해제
            const wrapper = document.querySelector('.whiteboardEx');
            if (wrapper) wrapper.classList.remove('hide-tool-panel');

            if (isOpenDiagramPopover) {
              // 도형 팝업 열린 상태에서 재클릭 → 팝업 닫고 커서로 전환
              setIsOpenDiagramPopover(false);
              onClickTools('selection');
            } else {
              // 도형 팝업 닫힌 상태에서 클릭 → 팝업 열기, 도구는 selection(임시)
              if (excalidrawApi?.current) {
                excalidrawApi.current.setActiveTool({ type: 'selection' });
              }
              setIsOpenDiagramPopover(true);
            }
          }}
          className="viewBtnItem--figure"
          title="도형"
        />
        <div className={`viewBtnItem__wrapLayer ${isOpenDiagramPopover ? 'open' : ''}`}>
          <strong className="wrapLayer__title">도형 선택</strong>
          <div className="wrapLayer__figureBox">
            <FigureButton
              figureType="rectangle"
              isSelected={currentTool === 'rectangle'}
              onClick={onClickTools}
              className="viewBtnItem--rec"
              title="네모"
            />
            <FigureButton
              figureType="ellipse"
              isSelected={currentTool === 'ellipse'}
              onClick={onClickTools}
              className="viewBtnItem--circle"
              title="원"
            />
            <FigureButton
              figureType="diamond"
              isSelected={currentTool === 'diamond'}
              onClick={onClickTools}
              className="viewBtnItem--rhombus"
              title="다이아몬드"
            />
          </div>
        </div>
      </div>
      <div className="viewBtnItem__wrap">
        <ToolButton
          toolType="line"
          isSelected={currentTool === 'line'}
          onClick={onClickTools}
          className="viewBtnItem--line"
          title="선"
        />
      </div>
      <div className="viewBtnItem__wrap">
        <ToolButton
          toolType="arrow"
          isSelected={currentTool === 'arrow'}
          onClick={onClickTools}
          className="viewBtnItem--arrow"
          title="화살표"
        />
      </div>
      <div className="viewBtnItem__wrap">
        <ToolButton
          toolType="text"
          isSelected={currentTool === 'text'}
          onClick={onClickTools}
          className="viewBtnItem--text"
          title="텍스트"
        />
      </div>
    </div>

    <div className="schechMode__viewBtnItem">
      <div className="viewBtnItem__wrap">
        <button type="button" onClick={onClickUndo} className="viewBtnItem viewBtnItem--rotateLeft" title="이전으로 복귀">
          <span className="blind">이전으로 복귀</span>
        </button>
      </div>
      <div className="viewBtnItem__wrap">
        <button type="button" onClick={onClickRedo} className="viewBtnItem viewBtnItem--rotateRight" title="재실행">
          <span className="blind">재실행</span>
        </button>
      </div>
    </div>
  </div>
);

// ─── PageNavigation (하단 페이지 썸네일) ───────────────────────────────────

interface PageNavigationProps {
  currentPage: number;  // 1-indexed
  totalPages: number;
  drawFileList: Array<{ thumbnailURL?: string }>;
  onClickPageNumber: (pageNumber: number) => void;
}

export const PageNavigation: React.FC<PageNavigationProps> = ({
  currentPage,
  totalPages,
  drawFileList,
  onClickPageNumber,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`viewer_sidebar ${isOpen ? 'open' : ''}`} style={{ zIndex: 601 }}>
      <button type="button" className="btn btn_sidebar" onClick={() => setIsOpen(!isOpen)}>
        <span className="sp_rtc">문서리스트 닫기</span>
      </button>
      <div className="custom_scroll" style={{ position: 'relative', overflow: 'hidden', width: '100%', height: '100%' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'scroll', marginRight: -15, marginBottom: -15 }}>
          <ul>
            {drawFileList.map((file, index) => (
              <li key={`page-nav-item-${index}`} className={index + 1 === currentPage ? 'on' : ''}>
                <button
                  type="button"
                  onClick={() => {
                    if (index + 1 !== currentPage) onClickPageNumber(index + 1);
                  }}
                  style={{ background: 'none', border: 'none' }}
                >
                  <span className="thumb">
                    {file.thumbnailURL ? (
                      <img src={file.thumbnailURL} style={{ maxWidth: 120 }} width="100%" alt={`페이지 ${index + 1}`} />
                    ) : (
                      <span style={{ display: 'block', width: 120, height: 80, background: '#f5f5f5' }} />
                    )}
                  </span>
                  <span className="pagenum">{index + 1}</span>
                  <span className="blind">파일 선택</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

// ─── SketchModeTitle (스케치 모드 배경/격자 설정) ──────────────────────────

interface SketchModeTitleProps {
  backgroundProps: {
    viewBackgroundColor: string;
    gridModeEnabled: boolean;
    gridSize: number;
  };
  onChangeBgColor: (color: string) => void;
  onChangeGrid: (gridModeEnabled: boolean, gridSize: number) => void;
  isOwner: boolean;
  editPermissions?: unknown;
  participants?: Array<{ isOwner?: boolean; jitsiId?: string; roomIndex?: number; canEdit?: boolean }>;
  onChangeEditorMode?: (isAllEditable: boolean) => void;
}

export const SketchModeTitle: React.FC<SketchModeTitleProps> = ({
  backgroundProps,
  onChangeBgColor,
  onChangeGrid,
  isOwner,
  editPermissions,
  participants = [],
  onChangeEditorMode,
}) => {
  const [isViewDropDown, setIsViewDropDown] = useState(false);
  const [isViewGroupPopover, setIsViewGroupPopover] = useState(false);
  const [isViewPopover, setIsViewPopover] = useState(false);
  const viewDropDownRef = useRef<HTMLDivElement>(null);
  const viewPopoverRef = useRef<HTMLDivElement>(null);
  const viewGroupPopoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isViewDropDown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (viewDropDownRef.current && !viewDropDownRef.current.contains(e.target as Node)) {
        setIsViewDropDown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isViewDropDown]);

  useEffect(() => {
    if (!isViewPopover) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (viewPopoverRef.current && !viewPopoverRef.current.contains(e.target as Node)) {
        setIsViewPopover(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isViewPopover]);

  useEffect(() => {
    if (!isViewGroupPopover) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (viewGroupPopoverRef.current && !viewGroupPopoverRef.current.contains(e.target as Node)) {
        setIsViewGroupPopover(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isViewGroupPopover]);

  // 1차 판단: 서버에서 온 초기 설정 (방장용 및 초기 상태)
  let isViewerMode = editPermissions !== null && Array.isArray(editPermissions) && editPermissions.length > 0;

  // 2차 판단(실시간 동기화): 일반 참가자는 방장이 권한을 바꿔도 서버 API(getRoomInfo)를 다시 부르지 않음.
  // Excalidraw가 동기화 해주는 참가자 권한(canEdit)으로 뷰어/공동편집 여부를 정확하게 파악.
  if (participants.length > 1) {
    const others = participants.filter(p => !p.isOwner);
    if (others.length > 0) {
      // 일반 참가자 중 편집 불가능한 사람이 한 명이라도 있으면 뷰어모드, 모두 편집 가능하면 공동편집
      isViewerMode = others.some(p => !p.canEdit);
    }
  }

  const local = useAppSelector((s) => s.local);
  const reduxParticipants = useAppSelector((s) => s.participants.participants);

  const getParticipantInfo = (jitsiId?: string) => {
    const defaultAvatar = 'https://static.wehago.com/imgs/common/profile_icon_user.png';
    const baseFallbackName = '참여자';

    if (!jitsiId) return { name: baseFallbackName, avatar: defaultAvatar };
    
    // 로컬 유저 검사 (Jitsi Endpoint ID 비교)
    const room = (window as any)._jitsiRoom;
    const localJitsiId = room?.myUserId?.();
    
    if (jitsiId === localJitsiId) {
      const dName = local.userName || local.nickname || local.userId || baseFallbackName;
      return { 
        name: dName, 
        avatar: local.avatarUrl || defaultAvatar 
      };
    }
    
    // 원격 유저 검사
    const p = reduxParticipants[jitsiId];
    if (p) {
      const dName = p.userName || p.displayName || p.nickname || baseFallbackName;
      return { 
        name: dName, 
        avatar: p.avatarUrl || defaultAvatar 
      };
    }
    
    return { name: baseFallbackName, avatar: defaultAvatar };
  };

  const currentBgColor = backgroundProps?.viewBackgroundColor ?? '#ffffff';
  let currentGridType = 'none';
  if (backgroundProps?.gridModeEnabled) {
    currentGridType = backgroundProps.gridSize === 40 ? 'type2' : 'type1';
  }

  const handleGrid = (type: string) => {
    if (type === 'type1') {
      onChangeGrid(true, 20);
    } else if (type === 'type2') {
      onChangeGrid(true, 40);
    } else {
      onChangeGrid(false, 20);
    }
  };

  return (
    <div className="schechMode__viewBtnTopLeft" style={{ zIndex: 12 }}>
      <div className="schechMode__viewBtnItem">
        <div ref={viewDropDownRef} className={`viewSetItem ${isViewDropDown ? 'open' : ''}`}>
          <button
            type="button"
            className="viewBtnItem viewBtnItem--setting"
            onClick={() => setIsViewDropDown(!isViewDropDown)}
          >
            <span className="blind">설정</span>
          </button>
          <div className="viewSetItem__list">
            <div className="viewSetItem__listItem">
              <strong className="title">배경색</strong>
              <div className="itemList">
                {BG_COLORS.map((bc) => (
                  <button
                    key={bc.value}
                    type="button"
                    className={`itemList__btn ${currentBgColor === bc.value ? 'selected' : ''}`}
                    onClick={() => onChangeBgColor(bc.value)}
                  >
                    <span className="bgItem" style={{ background: bc.value }} />
                    <span className="blind">{bc.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="viewSetItem__listItem">
              <strong className="title">눈금</strong>
              <div className="itemList">
                <button
                  type="button"
                  className={`itemList__btn ${currentGridType === 'none' ? 'selected' : ''}`}
                  onClick={() => handleGrid('none')}
                >
                  <span className="bgItem" style={{ background: '#fff' }} />
                  <span className="blind">눈금 없음</span>
                </button>
                <button
                  type="button"
                  className={`itemList__btn ${currentGridType === 'type2' ? 'selected' : ''}`}
                  onClick={() => handleGrid('type2')}
                >
                  <span className="bgItem graduation_type1" />
                </button>
                <button
                  type="button"
                  className={`itemList__btn ${currentGridType === 'type1' ? 'selected' : ''}`}
                  onClick={() => handleGrid('type1')}
                >
                  <span className="bgItem graduation_type2" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <p className="viewTextItem ellipsis">{local.roomName ? `${local.roomName} 스케치` : '회의 스케치'}</p>
        <span className="bar"></span>

        {/* 참여자 목록 드롭다운 */}
        <div ref={viewGroupPopoverRef} className={`viewGroupItem ${isViewGroupPopover ? 'open' : ''}`}>
          <button
            type="button"
            className="viewGroupItem__btn"
            onClick={() => setIsViewGroupPopover(!isViewGroupPopover)}
          >
            <span className="rtc_profileBox">
              {participants.slice(0, 3).map((p, idx) => {
                const info = getParticipantInfo(p.jitsiId);
                return (
                  <span key={p.jitsiId || idx} className="rtc_profileBox__img smallType">
                    <img src={info.avatar} alt={info.name} />
                  </span>
                )
              })}
              {participants.length === 0 && (
                <span className="rtc_profileBox__img emptyType smallType">
                  <img src="https://static.wehago.com/imgs/common/profile_icon_user.png" alt="" />
                </span>
              )}
              {participants.length > 3 && (
                <span className="rtc_profileBox__img textType smallType">
                  +{participants.length - 3}
                </span>
              )}
            </span>
          </button>
          
          <div className="viewGroupItem__list rtc_customScrollbar">
            {participants.length > 0 ? (
              participants.map((p, idx) => {
                const info = getParticipantInfo(p.jitsiId);
                return (
                  <div key={p.jitsiId || idx} className="viewGroupItem__listBtn">
                    <div className={`rtc_profileBox__img ${!p.jitsiId ? 'emptyType' : ''}`}>
                      <img src={info.avatar} alt={info.name} />
                    </div>
                    <span className="text ellipsis" title={info.name}>
                      {info.name}
                    </span>
                  </div>
                )
              })
            ) : (
              <div className="viewGroupItem__listBtn">
                <span className="rtc_profileBox__img emptyType">
                  <img src="https://static.wehago.com/imgs/common/profile_icon_user.png" alt="" />
                </span>
                <span className="text ellipsis">참여자 없음</span>
              </div>
            )}
          </div>
        </div>

        {/* 뷰어/공동편집 상태 표시 드롭다운 */}
        {isOwner && (
        <div ref={viewPopoverRef} className={`viewDropDownItem ${isViewPopover ? 'open' : ''}`}>
          <button
            type="button"
            className="viewDropDownItem__btn"
            onClick={() => {
              if (isOwner) {
                setIsViewPopover(!isViewPopover);
              }
            }}
            style={{ cursor: isOwner ? 'pointer' : 'default' }}
          >
            {isViewerMode ? '뷰어' : '공동 편집'}
          </button>
          
            <div className="viewDropDownItem__list">
              <button 
                type="button" 
                className={`viewDropDownItem__listBtn ${isViewerMode ? 'selected' : ''}`}
                onClick={() => {
                  onChangeEditorMode?.(false);
                  setIsViewPopover(false);
                }}
              >
                <span className="title">뷰어</span>
                <span className="desc">공유자외에 다른 참석자는 보기만 가능합니다.</span>
              </button>
              <button 
                type="button" 
                className={`viewDropDownItem__listBtn ${!isViewerMode ? 'selected' : ''}`}
                onClick={() => {
                  onChangeEditorMode?.(true);
                  setIsViewPopover(false);
                }}
              >
                <span className="title">공동 편집</span>
                <span className="desc">회의 참석하는 모든 사람이 편집할 수 있습니다</span>
              </button>
            </div>
        </div>
        )}
      </div>
    </div>
  );
};

// ─── ZoomControls (상단 줌 컨트롤) ─────────────────────────────────────────

interface ZoomControlsProps {
  zoomNumber: number;
  onClickZoomIn: () => void;
  onClickZoomOut: () => void;
}

export const ZoomControls: React.FC<ZoomControlsProps> = ({
  zoomNumber,
  onClickZoomIn,
  onClickZoomOut,
}) => (
  <div className="schechMode__viewBtn" style={{zIndex : 11}}>
    <div className="schechMode__viewBtnItem">
      <button type="button" className="viewBtnItem viewBtnItem--zoomout" onClick={onClickZoomOut}>
        <span className="blind">축소</span>
      </button>
      <span className="viewBtnItem viewBtnItem--zoomNumber">{Math.round(zoomNumber * 100)}%</span>
      <button type="button" className="viewBtnItem viewBtnItem--zoomin" onClick={onClickZoomIn}>
        <span className="blind">확대</span>
      </button>
    </div>
  </div>
);

// ─── SketchExpandButton (스케치 모드 우측 상단 크게보기) ─────────────────────

interface SketchExpandButtonProps {
  onClickExpand: () => void;
}

export const SketchExpandButton: React.FC<SketchExpandButtonProps> = ({ onClickExpand }) => (
  <div className="schechMode__viewBtnTopRight" style={{ zIndex: 2 }}>
    <div className="schechMode__viewBtnItem">
      <button type="button" className="viewBtnItem viewBtnItem--wide" onClick={onClickExpand}>
        <span className="blind">크게보기</span>
      </button>
    </div>
  </div>
);

// ─── DocShareHeader (문서공유 리뉴얼 상단 헤더) ────────────────────────────

interface DocShareHeaderProps {
  currentPage: number;    // 1-indexed
  totalPages: number;
  pdfFileName: string;
  isOwner: boolean;
  isViewOnly: boolean;
  isSideHidden: boolean;
  isSketchEnabled: boolean;
  onClickPrev: () => void;
  onClickNext: () => void;
  onToggleSideHide: () => void;
  onClickZoomIn: () => void;
  onClickZoomOut: () => void;
  onClickFullscreen: () => void;
  onToggleSketch: () => void;
}

export const DocShareHeader: React.FC<DocShareHeaderProps> = ({
  currentPage,
  totalPages,
  pdfFileName,
  isOwner,
  isViewOnly,
  isSideHidden,
  isSketchEnabled,
  onClickPrev,
  onClickNext,
  onToggleSideHide,
  onClickZoomIn,
  onClickZoomOut,
  onClickFullscreen,
  onToggleSketch,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <div className="shareViewMode__head">
      {/* 좌: 접기 + 파일명 */}
      <div className="shareViewMode__headTitle">
        <div className={`headTitle__dropdown ${isDropdownOpen ? 'open' : ''}`}>
          <button
            type="button"
            className="headTitle__dropdownBtn"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <span className={isSideHidden ? 'icon_foldRight' : 'icon_foldLeft'} />
            <span className="lunacon lunacon--arrowUp-outlined" />
          </button>
          <div className="viewerHeader__layer">
            <ul className="viewerHeader__layerList">
              <li>
                <button
                  type="button"
                  className={"viewerHeader__layerListBtn " + (isSideHidden ? "selected" : "")}
                  onClick={() => { onToggleSideHide(); setIsDropdownOpen(false); }}
                >
                  사이드바 가리기
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className={"viewerHeader__layerListBtn " + (!isSideHidden ? "selected" : "")}
                  onClick={() => { onToggleSideHide(); setIsDropdownOpen(false); }}
                >
                  썸네일
                </button>
              </li>
            </ul>
          </div>
        </div>
        <strong className="headTitle__text ellipsis">{pdfFileName}</strong>
      </div>

      {/* 중: 페이지 이동 */}
      <div className="shareViewMode__headPage">
        {currentPage <= 1 ? (
          <div className="pageBtn pageBtn--disabled"><span className="lunacon lunacon--arrowLeft-outlined" /></div>
        ) : (
          <button type="button" className="pageBtn" onClick={onClickPrev}><span className="lunacon lunacon--arrowLeft-outlined" /></button>
        )}
        <span className="pageText"><em>{currentPage}</em>/{totalPages}</span>
        {currentPage >= totalPages ? (
          <div className="pageBtn pageBtn--disabled"><span className="lunacon lunacon--arrowRight-outlined" /></div>
        ) : (
          <button type="button" className="pageBtn" onClick={onClickNext}><span className="lunacon lunacon--arrowRight-outlined" /></button>
        )}
      </div>

      {/* 우: 뷰어 옵션 */}
      <div className="shareViewMode__headOpt">
        <button type="button" className="headOpt__btn icon_expand" onClick={onClickFullscreen} title="전체보기">
          <span className="blind">전체보기</span>
        </button>
        <button type="button" className="headOpt__btn icon_zoomin" onClick={onClickZoomIn} title="확대">
          <span className="blind">확대</span>
        </button>
        <button type="button" className="headOpt__btn icon_zoomout" onClick={onClickZoomOut} title="축소">
          <span className="blind">축소</span>
        </button>
        {!isViewOnly && (
          <button
            type="button"
            className={`headOpt__btn icon_sketch ${isSketchEnabled ? 'selected' : ''}`}
            onClick={onToggleSketch}
            title="그리기"
          >
            <span className="blind">그리기</span>
          </button>
        )}
      </div>
    </div>
  );
};

// ─── DocShareSidebar (문서공유 리뉴얼 좌측 썸네일) ─────────────────────────

interface DocShareSidebarProps {
  currentPage: number;  // 1-indexed
  drawFileList: Array<{ thumbnailURL?: string }>;
  onClickPageNumber: (pageNumber: number) => void;
}

export const DocShareSidebar: React.FC<DocShareSidebarProps> = ({
  currentPage,
  drawFileList,
  onClickPageNumber,
}) => (
  <div className="shareViewMode__side">
    <ul className="preview_list common_customScrollbar">
      {drawFileList.map((file, index) => (
        <li key={`doc-thumb-${index}`} className={index + 1 === currentPage ? 'selected' : ''}>
          <button
            type="button"
            className="btn_preview"
            onClick={() => {
              if (index + 1 !== currentPage) onClickPageNumber(index + 1);
            }}
          >
            {file.thumbnailURL ? (
              <img src={file.thumbnailURL} className="img_preview" alt={`페이지 ${index + 1}`} />
            ) : (
              <span className="img_preview" style={{ display: 'block', background: '#333', minWidth: 120, minHeight: 80 }} />
            )}
            <span className="page_num">{index + 1}</span>
          </button>
        </li>
      ))}
    </ul>
  </div>
);

// ─── DocShareDrawTools (문서공유 가로 드로잉 도구) ──────────────────────────

interface DocShareDrawToolsProps {
  currentTool: string;
  onClickTools: (type: string) => void;
  isOpenDiagramPopover: boolean;
  setIsOpenDiagramPopover: (v: boolean) => void;
  excalidrawApi?: React.RefObject<{ setActiveTool: (tool: { type: string }) => void } | null>;
  onClickUndo: () => void;
  onClickRedo: () => void;
}

export const DocShareDrawTools: React.FC<DocShareDrawToolsProps> = ({
  currentTool,
  onClickTools,
  isOpenDiagramPopover,
  setIsOpenDiagramPopover,
  excalidrawApi,
  onClickUndo,
  onClickRedo,
}) => (
  <div className="schechMode__sideBox typeVertical" style={{zIndex:1002,top:"8px"}}>
    <div className="schechMode__viewBtnItem">
      <ToolButton
        toolType="selection"
        isSelected={currentTool === 'selection'}
        onClick={onClickTools}
        className="viewBtnItem--point"
        title="선택"
      />
      <div className="viewBtnItem__wrap">
        <ToolButton
          toolType="freedraw"
          isSelected={currentTool === 'freedraw'}
          onClick={onClickTools}
          className="viewBtnItem--pen"
          title="펜"
        />
      </div>
      <div className="viewBtnItem__wrap">
        <ToolButton
          toolType="text"
          isSelected={currentTool === 'text'}
          onClick={onClickTools}
          className="viewBtnItem--text"
          title="텍스트"
        />
      </div>
      <div className="viewBtnItem__wrap">
        <ToolButton
          toolType="eraser"
          isSelected={currentTool === 'eraser'}
          onClick={onClickTools}
          className="viewBtnItem--eraser"
          title="지우개"
        />
      </div>
      <div className="viewBtnItem__wrap">
        <ToolButton
          toolType="figure"
          isSelected={isOpenDiagramPopover || ['rectangle', 'ellipse', 'diamond'].includes(currentTool)}
          onClick={() => {
            const wrapper = document.querySelector('.whiteboardEx');
            if (wrapper) wrapper.classList.remove('hide-tool-panel');

            if (isOpenDiagramPopover) {
              setIsOpenDiagramPopover(false);
              onClickTools('selection');
            } else {
              if (excalidrawApi?.current) {
                excalidrawApi.current.setActiveTool({ type: 'selection' });
              }
              setIsOpenDiagramPopover(true);
            }
          }}
          className="viewBtnItem--figure"
          title="도형"
        />
        <div className={`viewBtnItem__wrapLayer ${isOpenDiagramPopover ? 'open' : ''}`}>
          <strong className="wrapLayer__title">도형 선택</strong>
          <div className="wrapLayer__figureBox">
            <FigureButton figureType="rectangle" isSelected={currentTool === 'rectangle'} onClick={onClickTools} className="viewBtnItem--rec" title="네모" />
            <FigureButton figureType="ellipse" isSelected={currentTool === 'ellipse'} onClick={onClickTools} className="viewBtnItem--circle" title="원" />
            <FigureButton figureType="diamond" isSelected={currentTool === 'diamond'} onClick={onClickTools} className="viewBtnItem--rhombus" title="마름모" />
          </div>
        </div>
      </div>
      <span className="bar" />
      <div className="viewBtnItem__wrap">
        <button type="button" onClick={onClickUndo} className="viewBtnItem viewBtnItem--rotateLeft" title="이전으로 복귀">
          <span className="blind">이전으로 복귀</span>
        </button>
      </div>
      <div className="viewBtnItem__wrap">
        <button type="button" onClick={onClickRedo} className="viewBtnItem viewBtnItem--rotateRight" title="재실행">
          <span className="blind">재실행</span>
        </button>
      </div>
    </div>
  </div>
);

// ─── Mobile WebView UI (독립 화이트보드 페이지) ───────────────────────────

export type MobileWhiteboardTool = 'selection' | 'pen' | 'highlighter' | 'eraser' | 'shape' | 'text' | 'template';
export type MobileWhiteboardShape =
  | 'rectangle'
  | 'roundRectangle'
  | 'ellipse'
  | 'diamond'
  | 'line'
  | 'arrow'
  | 'doubleArrow'
  | 'dashedLine';
export type MobileSelectionColorTarget = 'fill' | 'stroke';

export const MOBILE_SKETCH_COLORS = [
  '#000000',
  '#7600ff',
  '#2256ff',
  '#00a981',
  '#64eb78',
  '#ffed14',
  '#ff8800',
  '#ff3100',
] as const;

export const MOBILE_HIGHLIGHTER_COLORS = [
  '#a7a7a7',
  '#ceb7fa',
  '#a8bbff',
  '#aedfd6',
  '#bff4c8',
  '#fff4b3',
  '#ffc89d',
  '#ffb9b0',
] as const;

interface MobileSketchToolBoxProps {
  activeTool: MobileWhiteboardTool;
  activeShape: MobileWhiteboardShape;
  selectedPenColor: string;
  selectedHighlighterColor: string;
  selectedFillColor: string;
  selectedStrokeColor: string;
  lineWidth: number;
  eraserWidth: number;
  hasSelection: boolean;
  isTextSelection: boolean;
  isOpen: boolean;
  variant: 'sketch' | 'document';
  onSelectTool: (tool: MobileWhiteboardTool) => void;
  onSelectShape: (shape: MobileWhiteboardShape) => void;
  onSelectPenColor: (color: string) => void;
  onSelectHighlighterColor: (color: string) => void;
  onChangeSelectedElementColor: (target: MobileSelectionColorTarget, color: string) => void;
  onChangeLineWidth: (lineWidth: number) => void;
  onChangeEraserWidth: (lineWidth: number) => void;
  onDeleteSelection: () => void;
  onClose: () => void;
}

const MOBILE_SHAPE_BUTTONS: Array<{ shape: MobileWhiteboardShape; className: string; title: string }> = [
  { shape: 'rectangle', className: 'ico_rec', title: '각진 네모' },
  { shape: 'roundRectangle', className: 'ico_circlerec', title: '둥근 네모' },
  { shape: 'ellipse', className: 'ico_circle', title: '동그라미' },
  { shape: 'diamond', className: 'ico_rhombus', title: '마름모' },
  { shape: 'line', className: 'ico_line', title: '직선' },
  { shape: 'arrow', className: 'ico_arrow', title: '화살표' },
  { shape: 'doubleArrow', className: 'ico_arrow2', title: '양쪽 화살표' },
  { shape: 'dashedLine', className: 'ico_line2', title: '점선' },
];

const MOBILE_SHAPE_BUTTON_ROWS = [
  MOBILE_SHAPE_BUTTONS.slice(0, 4),
  MOBILE_SHAPE_BUTTONS.slice(4),
];

const getLineWidthPercent = (lineWidth: number) => `${Math.max(0, Math.min(100, ((lineWidth - 1) / 19) * 100))}%`;

const MobileColorPalette: React.FC<{
  colors: readonly string[];
  selectedColor: string;
  onSelectColor: (color: string) => void;
}> = ({ colors, selectedColor, onSelectColor }) => (
  <div className="schechTop__colorBox schechTop__colorBox--grid">
    {colors.map((color) => (
      <button
        key={color}
        type="button"
        className={`schechTop__colorBtn ${selectedColor.toLowerCase() === color.toLowerCase() ? 'selected' : ''}`}
        style={{ background: color }}
        onClick={() => onSelectColor(color)}
      >
        <span className="blind">색 선택</span>
      </button>
    ))}
  </div>
);

const MobileLineSlider: React.FC<{
  label: string;
  value: number;
  onChange: (lineWidth: number) => void;
}> = ({ label, value, onChange }) => (
  <div className="schechTop__lineBox">
    <strong>{label}</strong>
    <div className="wrapLayer__lineSlider">
      <div className="wrapLayer__lineSliderPoint" style={{ width: getLineWidthPercent(value) }}>
        <button type="button" className="wrapLayer__lineSliderBtn" tabIndex={-1}>
          <span className="blind">슬라이드버튼</span>
        </button>
      </div>
      <input
        type="range"
        min={1}
        max={20}
        value={value}
        aria-label={label}
        onChange={(event) => onChange(Number(event.target.value))}
        style={{
          position: 'absolute',
          top: -10,
          left: 0,
          width: '100%',
          height: 28,
          opacity: 0,
        }}
      />
    </div>
  </div>
);

export const MobileSketchToolBox: React.FC<MobileSketchToolBoxProps> = ({
  activeTool,
  activeShape,
  selectedPenColor,
  selectedHighlighterColor,
  selectedFillColor,
  selectedStrokeColor,
  lineWidth,
  eraserWidth,
  hasSelection,
  isTextSelection,
  isOpen,
  variant,
  onSelectTool,
  onSelectShape,
  onSelectPenColor,
  onSelectHighlighterColor,
  onChangeSelectedElementColor,
  onChangeLineWidth,
  onChangeEraserWidth,
  onDeleteSelection,
  onClose,
}) => {
  const [selectionColorTarget, setSelectionColorTarget] = useState<MobileSelectionColorTarget | null>(null);
  const isSelectionMode = (activeTool === 'selection' || activeTool === 'template') && hasSelection;
  const isToolSelected = (tool: MobileWhiteboardTool) => activeTool === tool;

  useEffect(() => {
    if (!isSelectionMode) {
      setSelectionColorTarget(null);
    }
  }, [isSelectionMode]);

  let topContent: React.ReactNode = null;

  if (activeTool === 'pen') {
    topContent = (
      <>
        <MobileColorPalette colors={MOBILE_SKETCH_COLORS} selectedColor={selectedPenColor} onSelectColor={onSelectPenColor} />
        <MobileLineSlider label="선 굵기" value={lineWidth} onChange={onChangeLineWidth} />
      </>
    );
  } else if (activeTool === 'highlighter') {
    topContent = (
      <>
        <MobileColorPalette colors={MOBILE_HIGHLIGHTER_COLORS} selectedColor={selectedHighlighterColor} onSelectColor={onSelectHighlighterColor} />
        <MobileLineSlider label="선 굵기" value={lineWidth} onChange={onChangeLineWidth} />
      </>
    );
  } else if (activeTool === 'eraser') {
    // topContent = <MobileLineSlider label="지우개 굵기" value={eraserWidth} onChange={onChangeEraserWidth} />;
  } else if (isSelectionMode && selectionColorTarget) {
    const isFillTarget = selectionColorTarget === 'fill';
    topContent = (
      <MobileColorPalette
        colors={isFillTarget ? MOBILE_HIGHLIGHTER_COLORS : MOBILE_SKETCH_COLORS}
        selectedColor={isFillTarget ? selectedFillColor : selectedStrokeColor}
        onSelectColor={(color) => onChangeSelectedElementColor(selectionColorTarget, color)}
      />
    );
  }

  const footerContent = activeTool === 'shape' ? (
    <>
      {MOBILE_SHAPE_BUTTON_ROWS.map((row, rowIndex) => (
        <div key={`mobile-shape-row-${rowIndex}`} className="rtcMobile__schechBoxFooter">
          {row.map((button) => (
            <button
              key={button.shape}
              type="button"
              className={`schechBox__btn ${button.className} ${activeShape === button.shape ? 'selected' : ''}`}
              onClick={() => onSelectShape(button.shape)}
              title={button.title}
            >
              <span className="blind">{button.title}</span>
            </button>
          ))}
          {rowIndex === MOBILE_SHAPE_BUTTON_ROWS.length - 1 && (
            <button type="button" className="schechBox__btn ico_close" onClick={() => onSelectTool('selection')}>
              <span className="blind">닫기</span>
            </button>
          )}
        </div>
      ))}
    </>
  ) : isSelectionMode ? (
    <div className="rtcMobile__schechBoxFooter type2">
      {isTextSelection ? (
        <button
          type="button"
          className={`schechBox__btn ico_footer2 ${selectionColorTarget === 'stroke' ? 'selected' : ''}`}
          onClick={() => setSelectionColorTarget('stroke')}
        >
          <span className="schechBox__btnText ellipsis">글자색</span>
        </button>
      ) : (
        <>
          <button
            type="button"
            className={`schechBox__btn ico_footer1 ${selectionColorTarget === 'fill' ? 'selected' : ''}`}
            onClick={() => setSelectionColorTarget('fill')}
          >
            <span className="schechBox__btnText ellipsis">채우기색</span>
          </button>
          <button
            type="button"
            className={`schechBox__btn ico_footer2 ${selectionColorTarget === 'stroke' ? 'selected' : ''}`}
            onClick={() => setSelectionColorTarget('stroke')}
          >
            <span className="schechBox__btnText ellipsis">테두리색</span>
          </button>
        </>
      )}
      <button
        type="button"
        tabIndex={-1}
        className="schechBox__btn ico_footer4"
        onTouchStart={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onTouchEnd={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setSelectionColorTarget(null);
          onDeleteSelection();
        }}
        onMouseUp={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setSelectionColorTarget(null);
          onDeleteSelection();
        }}
      >
        <span className="schechBox__btnText ellipsis">삭제</span>
      </button>
    </div>
  ) : (
    <div className={`rtcMobile__schechBoxFooter ${variant === 'sketch' ? 'typeMain' : ''}`}>
      <button
        type="button"
        className={`schechBox__btn ico_pen ${isToolSelected('pen') ? 'selected' : ''}`}
        onClick={() => onSelectTool('pen')}
      >
        <span className="blind">펜</span>
      </button>
      <button
        type="button"
        className={`schechBox__btn ico_lightpen ${isToolSelected('highlighter') ? 'selected' : ''}`}
        onClick={() => onSelectTool('highlighter')}
      >
        <span className="blind">형광펜</span>
      </button>
      <button
        type="button"
        className={`schechBox__btn ico_eraser ${isToolSelected('eraser') ? 'selected' : ''}`}
        onClick={() => onSelectTool('eraser')}
      >
        <span className="blind">지우개</span>
      </button>
      {variant === 'sketch' && (
        <>
          <button
            type="button"
            className={`schechBox__btn ico_shape ${isToolSelected('shape') ? 'selected' : ''}`}
            onClick={() => onSelectTool('shape')}
          >
            <span className="blind">도형</span>
          </button>
          <button
            type="button"
            className={`schechBox__btn ico_text ${isToolSelected('text') ? 'selected' : ''}`}
            onClick={() => onSelectTool('text')}
          >
            <span className="blind">텍스트</span>
          </button>
          {/* <button
            type="button"
            className={`schechBox__btn ico_templete ${isToolSelected('selection') ? 'selected' : ''}`}
            onClick={() => onSelectTool('selection')}
          >
            <span className="blind">선택</span>
          </button> */}
        </>
      )}
      {variant === 'document' && (
        <button type="button" className="schechBox__btn ico_close" onClick={onClose}>
          <span className="blind">닫기</span>
        </button>
      )}
    </div>
  );

  return (
    <div className={`rtcMobile__schechBox ${isOpen ? 'open' : ''}`}>
      {topContent && <div className="rtcMobile_schechBoxTop">{topContent}</div>}
      {footerContent}
    </div>
  );
};

interface MobileDocumentSidebarProps {
  currentPage: number;
  files: Array<{ dataURL?: string; thumbnailURL?: string }>;
  onClickPageNumber: (pageNumber: number) => void;
}

export const MobileDocumentSidebar: React.FC<MobileDocumentSidebarProps> = ({
  currentPage,
  files,
  onClickPageNumber,
}) => (
  <div className="rtcMobile_docview__side">
    <ul className="preview_list">
      {files.map((file, index) => (
        <li key={`mobile-doc-thumb-${index}`} className={index + 1 === currentPage ? 'selected' : ''}>
          <button
            type="button"
            className="btn_preview"
            onClick={() => {
              if (index + 1 !== currentPage) onClickPageNumber(index + 1);
            }}
          >
            {file.dataURL || file.thumbnailURL ? (
              <img src={file.dataURL || file.thumbnailURL} className="img_preview" alt={`페이지 ${index + 1}`} />
            ) : (
              <span className="img_preview" style={{ display: 'block', width: 92, height: 120, background: '#f5f5f5' }} />
            )}
            <span className="page_num">{index + 1}</span>
          </button>
        </li>
      ))}
    </ul>
  </div>
);

interface MobilePermissionPopupProps {
  isOpen: boolean;
  isAllEditable: boolean;
  onChangeMode: (isAllEditable: boolean) => void;
  onApply: () => void;
}

export const MobilePermissionPopup: React.FC<MobilePermissionPopupProps> = ({
  isOpen,
  isAllEditable,
  onChangeMode,
  onApply,
}) => {
  if (!isOpen) return null;

  return (
    <div className="rtcMobile_popup">
      <div className="rtcMobile_popupBox">
        <div className="rtcMobile_popupBoxTitle">
          <h2>스케치 권한 설정</h2>
        </div>
        <div className="rtcMobile_popupBoxCon">
          <dl className="rtcMobile_popupBoxCon__dl">
            <div>
              <dt>
                <div className="common_radio_input type_agree5">
                  <label>
                    <input
                      className="radio_input"
                      type="checkbox"
                      checked={isAllEditable}
                      onChange={() => onChangeMode(true)}
                    />
                    <span className="icon" />
                    <span className="label_text">공동편집</span>
                  </label>
                </div>
              </dt>
              <dd>회의에 참석하는 모든 사람이 편집할 수 있습니다.</dd>
            </div>
            <div>
              <dt>
                <div className="common_radio_input type_agree5">
                  <label>
                    <input
                      className="radio_input"
                      type="checkbox"
                      checked={!isAllEditable}
                      onChange={() => onChangeMode(false)}
                    />
                    <span className="icon" />
                    <span className="label_text">뷰어</span>
                  </label>
                </div>
              </dt>
              <dd>소유자 외에 다른 참석자는 보기만 가능하며 편집할 수 없습니다.</dd>
            </div>
          </dl>
          <div className="rtcMobile_popupBoxCon__info">
            <p>스케치 모드 종료 시에도 위 권한은 유지되며 권한변경이 필요한 경우 ‘스케치’ 메뉴에서 가능합니다.</p>
          </div>
        </div>
        <div className="rtcMobile_popupBoxFooter">
          <button type="button" className="footerBtn" onClick={onApply}>적용</button>
        </div>
      </div>
    </div>
  );
};

interface MobileWhiteboardLoadingProps {
  progress?: number;
}

export const MobileWhiteboardLoading: React.FC<MobileWhiteboardLoadingProps> = ({ progress = 50 }) => (
  <div id="wrap">
    <div className="rtcMobile pretendard_font">
      <div className="rtcMobile_schechMode" />
      <div className="rtcMobile_schechMode__loading">
        <p className="loadingText">스케치를 불러오는중...</p>
        <div className="loadingBox">
          <span className="loadingBox__progress" style={{ width: `${progress}%` }} />
        </div>
      </div>
    </div>
  </div>
);

interface MobileSketchToastProps {
  message: string | null;
  onRetry?: () => void;
}

export const MobileSketchToast: React.FC<MobileSketchToastProps> = ({ message, onRetry }) => {
  if (!message) return null;

  return (
    <div className="rtcMobile__schechToast">
      <div className="rtcMobile__schechToastBox">
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="9" cy="9" r="6.5" stroke="#fff" />
          <path d="m6.5 9 1.918 2.082L12.5 7" stroke="#fff" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <p className="rtcMobile__schechToastText">{message}</p>
        {onRetry && (
          <button type="button" className="rtcMobile__schechToastBtn" onClick={onRetry}>
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x=".5" y=".5" width="35" height="35" rx="5.5" stroke="#fff" strokeOpacity=".4" />
              <path d="M29.208 17.43 26.6 20.038M26.6 20.038l-2.608-2.608M25.114 22.95a8.668 8.668 0 1 1 1.452-3.65" stroke="#fff" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" />
            </svg>
            <span className="blind">새로고침</span>
          </button>
        )}
      </div>
    </div>
  );
};
